// Allocation rules — live /cashbook/rules parity: match text (stem), TYPE cycles on
// click (Exact → Starts with → Fuzzy), SCOPE toggles Global ↔ a bank account, target
// GL/contact, hit counter, inline delete, add-rule form.
import { html, signal, navigate } from '../shared/t4.js';
import { icon } from '../shared/icons.js';
import { api } from '../shared/api.js';
import { rowDelete } from '../shared/confirm.js';
import { tenantShell } from './tshell.js';
import { cycleMatchType, MATCH_TYPE_LABELS, formatGlCode } from './bank_helpers.js';
import { t } from '../shared/i18n.js';

// Pure: build the PUT body for an inline rule edit from the edit-form state, or an error.
// Mirrors the add form's fields (stem + type + scope + GL target); a GL-targeted edit clears any
// contact target so update_rule re-validates the pair. Exported for the DOM test (real gate).
export function ruleEditPayload(editState){
  const stem = ((editState && editState.stem) || '').trim();
  const account_id = editState && editState.gl ? Number(editState.gl) : null;
  if(!stem || !account_id) return { error: t('bank_rules.err_required', 'Rule text and a GL account are required.') };
  return { payload: {
    match_stem: stem,
    match_type: (editState && editState.type) || 'fuzzy',
    account_id,
    contact_id: null,
    bank_account_id: editState && editState.scope ? Number(editState.scope) : null,
  } };
}

export function bankRulesScreen(companyId){
  const rules = signal([]), accounts = signal([]), reference = signal(null);
  const loading = signal(true), err = signal(''), notice = signal('');

  const fStem = signal(''), fType = signal('fuzzy'), fScope = signal(''), fGl = signal('');
  const saving = signal(false), formErr = signal('');
  // Clear the "required" banner as the user fills a field, rather than leaving it up until the next
  // Add. Mirrors the invoice editor's per-field clearInvalid (see CLAUDE.md).
  const clearFormErr = () => { if(formErr.value) formErr.value = ''; };
  const confirmId = signal(null), deleting = signal(false), delErr = signal('');
  // Inline edit (Feature E): editId is the rule being edited; the eXxx signals hold the draft.
  const editId = signal(null), eStem = signal(''), eType = signal('fuzzy'),
        eScope = signal(''), eGl = signal(''), editErr = signal(''), savingEdit = signal(false);
  const applyingId = signal(null);          // rule whose "apply to existing" call is in flight
  const search = signal('');
  let searchTimer = null;

  async function load(){
    try {
      const r = await api.get('/companies/' + companyId + '/bank-rules');
      rules.value = r.data || [];
      const a = await api.get('/companies/' + companyId + '/bank-accounts');
      accounts.value = (a.data || []).filter(c => c.is_active);
      err.value = '';
    } catch(e){ err.value = (e.data && e.data.error) || t('bank_rules.err_load', 'Could not load rules'); }
    loading.value = false;
  }
  load();
  (async () => { try { reference.value = await api.get('/companies/' + companyId + '/bank-reference'); } catch {} })();

  const glAccounts = () => ((reference.value && reference.value.accounts) || [])
    .filter(a => a.is_postable && !a.is_control && !a.is_bank && a.code !== '0000.000' && a.code !== '0000');
  // ledger-code display: base codes flat (9500); real sub-accounts keep their dot
  const gcode = code => formatGlCode(code);

  function toast(message){ notice.value = message; setTimeout(() => { if(notice.value === message) notice.value = ''; }, 4000); }

  // Rule refiner (v2 parity): consolidate near-duplicate rules that map to the same target.
  const refining = signal(false);
  async function doRefine(){
    refining.value = true;
    try {
      const res = await api.post('/companies/' + companyId + '/bank-rules/refine', {}) || {};
      const merged = res.merged ?? res.applied ?? res.unambiguous ?? 0;
      toast(merged ? t('bank_rules.merged', '{n} duplicate rule(s) merged').replace('{n}', merged)
                   : t('bank_rules.none_to_merge', 'No duplicate rules to merge'));
      await load();
    } catch(e){ toast((e.data && e.data.error) || t('bank_rules.err_refine', 'Could not merge duplicates')); }
    refining.value = false;
  }

  async function addRule(e){
    e.preventDefault();
    if(!fStem.value.trim() || !fGl.value){ formErr.value = t('bank_rules.err_required', 'Rule text and a GL account are required.'); return; }
    saving.value = true; formErr.value = '';
    try {
      await api.post('/companies/' + companyId + '/bank-rules', {
        match_stem: fStem.value.trim(), account_id: Number(fGl.value),
        match_type: fType.value, bank_account_id: fScope.value ? Number(fScope.value) : null });
      fStem.value = '';
      await load();
    } catch(e2){ formErr.value = (e2.data && e2.data.error) || e2.message || t('bank_rules.err_add', 'Could not add the rule'); }
    saving.value = false;
  }

  // live behaviour: clicking TYPE cycles it, clicking SCOPE toggles global ↔ this account
  async function cycleType(rule){
    const next = cycleMatchType(rule.match_type);
    try { await api.put('/companies/' + companyId + '/bank-rules/' + rule.id, { match_type: next }); await load(); }
    catch(e){ toast((e.data && e.data.error) || t('bank_rules.err_type', 'Could not change the type')); }
  }
  async function toggleScope(rule){
    const next = rule.bank_account_id ? null
      : (accounts.value[0] ? accounts.value[0].id : null);
    if(!rule.bank_account_id && !next){ toast(t('bank_rules.err_need_account', 'Add a bank account first to scope a rule to it')); return; }
    try { await api.put('/companies/' + companyId + '/bank-rules/' + rule.id, { bank_account_id: next }); await load(); }
    catch(e){ toast((e.data && e.data.error) || t('bank_rules.err_scope', 'Could not change the scope')); }
  }
  // Feature E — inline edit of a rule's match pattern + target GL (type/scope already flip inline)
  function startEdit(rule){
    editErr.value = ''; editId.value = rule.id;
    eStem.value = rule.match_stem || '';
    eType.value = rule.match_type || 'fuzzy';
    eScope.value = rule.bank_account_id ? String(rule.bank_account_id) : '';
    eGl.value = rule.account_id ? String(rule.account_id) : '';
  }
  function cancelEdit(){ editId.value = null; editErr.value = ''; }
  async function saveEdit(rule){
    const built = ruleEditPayload({ stem: eStem.value, type: eType.value,
      scope: eScope.value, gl: eGl.value });
    if(built.error){ editErr.value = built.error; return; }
    savingEdit.value = true; editErr.value = '';
    try {
      await api.put('/companies/' + companyId + '/bank-rules/' + rule.id, built.payload);
      editId.value = null;
      await load();
      toast(t('bank_rules.updated', 'Rule updated'));
    } catch(e){ editErr.value = (e.data && e.data.error) || e.message || t('bank_rules.err_update', 'Could not update the rule'); }
    savingEdit.value = false;
  }

  // Feature F — re-apply a rule across the company's existing bank lines (posted or not)
  async function applyRule(rule){
    applyingId.value = rule.id;
    try {
      const r = await api.post('/companies/' + companyId + '/bank-rules/' + rule.id + '/apply', {});
      toast(t('bank_rules.applied', 'Applied to existing: {a} re-allocated, {p} re-posted, {s} skipped (locked)')
        .replace('{a}', r.reallocated_unposted || 0).replace('{p}', r.reposted || 0)
        .replace('{s}', r.skipped_locked || 0));
      await load();
    } catch(e){ toast((e.data && e.data.error) || t('bank_rules.err_apply', 'Could not apply the rule')); }
    applyingId.value = null;
  }

  function armDelete(id){ delErr.value = ''; confirmId.value = id; }
  function cancelDelete(){ confirmId.value = null; delErr.value = ''; }
  async function doDelete(id){
    deleting.value = true; delErr.value = '';
    try { await api.del('/companies/' + companyId + '/bank-rules/' + id); confirmId.value = null; await load(); }
    catch(e){ delErr.value = (e.data && e.data.error) || t('common.delete_failed', 'Delete failed'); }
    deleting.value = false;
  }

  const list = () => {
    if(loading.value) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    if(err.value) return html`<div class="muted-block">${err.value}</div>`;
    const s = search.value.trim().toLowerCase();
    const rows = !s ? rules.value : rules.value.filter(r =>
      (r.match_stem || '').toLowerCase().includes(s)
      || (r.account_name || '').toLowerCase().includes(s)
      || (r.account_code || '').toLowerCase().includes(s)
      || (r.contact_name || '').toLowerCase().includes(s));
    const note = notice.value;
    const confirming = confirmId.value, busyDel = deleting.value, delNote = delErr.value;
    const eid = editId.value, eBusy = savingEdit.value, eNote = editErr.value;
    const applying = applyingId.value;
    const editRow = r => html`
        <div class="row-card rule-card rule-card-edit">
          <div class="rule-edit-form">
            <input class="form-control" .value=${eStem} @input=${e => { editErr.value = ''; eStem.value = e.target.value; }} placeholder=${() => t('bank_rules.stem_placeholder', 'e.g. DISCHEM PHAR')}>
            <select class="form-control" .value=${eType} @change=${e => { eType.value = e.target.value; }}>
              <option value="fuzzy">Fuzzy</option>
              <option value="starts_with">Starts with</option>
              <option value="exact">Exact</option>
              <option value="nl">Natural language (AI)</option>
            </select>
            <select class="form-control" .value=${eScope} @change=${e => { eScope.value = e.target.value; }}>
              <option value="">${() => t('common.global', 'Global')}</option>
              ${() => accounts.value.map(a => html`<option value=${a.id}>${a.name}</option>`)}
            </select>
            <select class="form-control" .value=${eGl} @change=${e => { editErr.value = ''; eGl.value = e.target.value; }}>
              <option value="">${() => t('bank_rules.allocate_gl_placeholder', '— Allocate to GL —')}</option>
              ${() => glAccounts().map(a => html`<option value=${a.id}>${gcode(a.code) + ' — ' + a.name}</option>`)}
            </select>
            <div class="rule-edit-actions">
              <button type="button" class="btn btn-outline btn-sm" @click=${cancelEdit}>${t('common.cancel', 'Cancel')}</button>
              <button type="button" class="btn btn-primary btn-sm" ?disabled=${eBusy} @click=${() => saveEdit(r)}>${eBusy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
            </div>
            ${eNote ? html`<div class="row-err rule-form-err">${eNote}</div>` : ''}
          </div>
        </div>`;
    const showRow = r => html`
        <div class="row-card rule-card">
          <span class="row-main">
            <b>${r.match_stem}</b>
            <span class="row-sub">${'→ ' + (r.account_code ? gcode(r.account_code) + ' ' + (r.account_name || '') : (r.contact_name || '—'))}${r.hits ? ' · ' + r.hits + ' hits' : ''}</span>
          </span>
          <span class="row-actions">
            <button type="button" class="chip-btn" title=${t('bank_rules.type_hint', 'Click to change the match type')} @click=${() => cycleType(r)}>${MATCH_TYPE_LABELS[r.match_type] || r.match_type}</button>
            <button type="button" class="chip-btn" title=${t('bank_rules.scope_hint', 'Click to flip the scope')} @click=${() => toggleScope(r)}>${r.bank_account_id ? (r.bank_account_name || t('bank_rules.scope_this', 'This account')) : t('common.global', 'Global')}</button>
            <button type="button" class="chip-btn" title=${t('bank_rules.apply_hint', 'Apply this rule to existing transactions')} ?disabled=${applying === r.id} @click=${() => applyRule(r)}>${applying === r.id ? t('common.working', 'Working…') : t('bank_rules.apply', 'Apply to existing')}</button>
            <button type="button" class="chip-btn" title=${t('bank_rules.edit_hint', 'Edit this rule')} @click=${() => startEdit(r)}>${icon('pencil', {size: 13})}</button>
            ${rowDelete({ armed: confirming === r.id, busy: busyDel,
                          error: confirming === r.id ? delNote : '',
                          onArm: () => armDelete(r.id), onCancel: cancelDelete, onDelete: () => doDelete(r.id) })}
          </span>
        </div>`;
    return html`<div>
      ${note ? html`<div class="alert alert-ok py-2 mb-2">${note}</div>` : ''}
      ${!rows.length && s ? html`<div class="muted-block">${t('bank_rules.no_match', 'No rules match')} “${s}”.</div>` : ''}
      ${rows.length ? html`<div class="row-list">${rows.map(r => eid === r.id ? editRow(r) : showRow(r))}</div>`
      : html`<div class="muted-block">${t('bank_rules.empty', 'No rules yet — allocating a GL account to a bank line creates one automatically.')}</div>`}
    </div>`;
  };

  const content = html`<div class="page">
    <div class="page-back"><a class="back-link" href=${`/co/${companyId}/bank`}
      @click=${e => { e.preventDefault(); navigate(`/co/${companyId}/bank`); }}>${icon('chevron-left', {size: 14})} ${() => t('common.back_to_bank', 'Back to Bank Accounts')}</a></div>
    <div class="page-head"><div><h1 class="page-title">${() => t('bank_rules.title', 'Allocation rules')}</h1>
      <div class="page-sub">${() => rules.value.length + (rules.value.length === 1 ? ' rule' : ' rules')}</div></div>
      <button type="button" class="btn btn-outline btn-sm" ?disabled=${() => refining.value}
        @click=${doRefine} title=${() => t('bank_rules.merge_hint', 'Consolidate near-duplicate rules that map to the same account')}>${() => refining.value ? t('common.working', 'Working…') : t('bank_rules.merge_dupes', 'Merge duplicates')}</button></div>
    <form class="card mb-2" @submit=${addRule}>
      <div class="card-body rule-form">
        <input class="form-control" .value=${fStem} @input=${e => { clearFormErr(); fStem.value = e.target.value; }} placeholder=${() => t('bank_rules.stem_placeholder', 'e.g. DISCHEM PHAR')}>
        <select class="form-control" .value=${fType} @change=${e => { fType.value = e.target.value; }}>
          <option value="fuzzy">Fuzzy</option>
          <option value="starts_with">Starts with</option>
          <option value="exact">Exact</option>
          <option value="nl">Natural language (AI)</option>
        </select>
        <select class="form-control" .value=${fScope} @change=${e => { fScope.value = e.target.value; }}>
          <option value="">${() => t('common.global', 'Global')}</option>
          ${() => accounts.value.map(a => html`<option value=${a.id}>${a.name}</option>`)}
        </select>
        <select class="form-control" .value=${fGl} @change=${e => { clearFormErr(); fGl.value = e.target.value; }}>
          <option value="">${() => t('bank_rules.allocate_gl_placeholder', '— Allocate to GL —')}</option>
          ${() => glAccounts().map(a => html`<option value=${a.id}>${gcode(a.code) + ' — ' + a.name}</option>`)}
        </select>
        <button type="submit" class="btn btn-primary" ?disabled=${() => saving.value}>${() => saving.value ? t('common.adding', 'Adding…') : t('bank_rules.add_rule', '+ Add rule')}</button>
        ${() => formErr.value ? html`<div class="row-err rule-form-err">${formErr}</div>` : null}
      </div>
    </form>
    <div class="rule-search-bar">
      <input id="ruleSearch" class="form-control" type="search"
        placeholder=${() => t('bank_rules.search_placeholder', 'Search rules… e.g. dischem or 7100')}
        @input=${e => { const v = e.target.value; clearTimeout(searchTimer);
          searchTimer = setTimeout(() => { search.value = v;
            setTimeout(() => { const el = document.getElementById('ruleSearch');
              if(el && document.activeElement !== el){ el.focus(); try { el.setSelectionRange(v.length, v.length); } catch { /* search quirks */ } } }, 0);
          }, 200); }}>
    </div>
    ${list}
  </div>`;

  return tenantShell(companyId, 'Allocation rules', content);
}
